import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtPeriod, useApi } from "@/lib/api";
import { StatusPill, fmtDate, usd } from "@/console/util";

const Kpi = ({ label, value, testid }) => (
  <div className="panel p-5">
    <div className="small-label">{label}</div>
    <div className="num text-2xl font-semibold mt-2" data-testid={testid}>{value}</div>
  </div>
);

function NewFirm({ open, onClose, onDone }) {
  const api = useApi();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const save = async () => {
    try {
      const r = await api.post("/sa/firms", { name, admin_email: email || null });
      const inv = r.data.invite;
      if (!email) toast.success("Firm created");
      else if (inv?.email_delivered_now) toast.success("Firm created — welcome email sent to the admin");
      else toast.warning("Firm created and admin invited, but the welcome email couldn't be sent. Use Resend on the firm page.");
      setName("");
      setEmail("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="new-firm-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">New firm</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Firm name" className="rounded-sm" data-testid="new-firm-name-input" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="First admin's email (optional)" className="rounded-sm" data-testid="new-firm-admin-input" />
        <p className="text-xs text-slate-500">The admin gets a welcome email with a sign-up link and lands in the firm when they sign up with this email. The firm starts unsubscribed (view-only) unless you mark it complimentary.</p>
        <Button onClick={save} disabled={name.trim().length < 2} className="rounded-sm" data-testid="new-firm-save-button">Create firm</Button>
      </DialogContent>
    </Dialog>
  );
}

function BuildDemo({ open, onClose, onDone }) {
  const api = useApi();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sa/demo", { password: pw });
      toast.success(`Demo firm ready: ${r.data.entities} entities, ${r.data.periods} month ends. Sign in as ${r.data.email}`);
      setPw("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="build-demo-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">Build demo firm</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Creates (or resets) <b>Rockefeller Family</b>: John D. and Emily S. Rockefeller 50/50, 7 LLCs and LPs (energy and operating businesses), 3 children's trusts and 6 month ends of sample balances. Demo admin login: <b>admin@ledgerline-demo.com</b>.</p>
        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Demo password (8+ characters)" className="rounded-sm font-mono" data-testid="build-demo-password-input" />
        <Button onClick={go} disabled={busy || pw.length < 8} className="rounded-sm" data-testid="build-demo-save-button">{busy ? "Building…" : "Build demo firm"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsoleFirms() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => api.get("/sa/overview").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api]);

  useEffect(() => {
    load();
  }, [load]);

  if (!d) return <p className="small-label">Loading…</p>;
  const t = d.totals;
  return (
    <div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Firms</h1>
          <p className="text-slate-500 mt-2 text-sm">Accounts, usage and billing for every tenant. Financial statements stay private to each firm.</p>
        </div>
        <Button variant="outline" onClick={() => setDemoOpen(true)} className="ml-auto rounded-sm" data-testid="build-demo-button">Build demo firm</Button>
        <Button onClick={() => setOpen(true)} className="rounded-sm" data-testid="new-firm-button"><Plus className="h-4 w-4 mr-1" />New firm</Button>
        <BuildDemo open={demoOpen} onClose={() => setDemoOpen(false)} onDone={() => { setDemoOpen(false); load(); }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-8">
        <Kpi label="Firms" value={t.firms} testid="kpi-firms" />
        <Kpi label="Paying" value={t.paying} testid="kpi-paying" />
        <Kpi label="On trial" value={t.trialing} testid="kpi-trialing" />
        <Kpi label="Complimentary" value={t.complimentary} testid="kpi-comped" />
        <Kpi label="View-only" value={t.read_only} testid="kpi-readonly" />
        <Kpi label="Recurring / month" value={usd(t.mrr)} testid="kpi-mrr" />
      </div>
      {t.archived > 0 && (
        <label className="flex items-center gap-2 mt-6 text-sm text-slate-600">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived-checkbox" />
          Show {t.archived} archived firm{t.archived > 1 ? "s" : ""}
        </label>
      )}
      <div className="panel mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]" data-testid="console-firms-table">
          <thead className="bg-slate-100">
            <tr className="small-label text-left">
              <th className="px-5 py-3">Firm</th><th>Billing</th><th>Plan</th><th className="text-right">Logins</th>
              <th className="text-right">Entities</th><th className="pl-8">Latest month end</th><th className="pl-4">Created</th><th className="pr-5" />
            </tr>
          </thead>
          <tbody>
            {d.firms.filter((f) => showArchived || !f.billing.archived).map((f) => (
              <tr key={f.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-200" data-testid={`console-firm-row-${f.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{f.name}{f.signup === "self" && <span className="ml-2 text-[10px] font-mono uppercase text-slate-400">self sign-up</span>}</td>
                <td><StatusPill billing={f.billing} testid={`console-firm-status-${f.id}`} /></td>
                <td className="text-xs text-slate-600">{f.billing.amount ? `${usd(f.billing.amount)} / ${f.billing.interval}` : "—"}</td>
                <td className="num text-right">{f.users}{f.pending_invites > 0 && <span className="text-xs text-slate-400"> +{f.pending_invites}</span>}</td>
                <td className="num text-right">{f.entities}</td>
                <td className="font-mono text-xs pl-8">{f.latest_period ? fmtPeriod(f.latest_period) : <span className="text-slate-400">No uploads</span>}</td>
                <td className="text-xs text-slate-500 pl-4">{fmtDate(f.created_at)}</td>
                <td className="pr-5 text-right"><Link to={`/console/firms/${f.id}`} className="text-sm underline text-slate-700 hover:text-amber-700" data-testid={`console-firm-manage-${f.id}`}>Manage</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <NewFirm open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />
    </div>
  );
}
